import { CLM } from "@/common/schema/claims";
import { RLT } from "@/common/schema/role-types";
import * as CFG_DATA from "@/be/configuration/configuration-data";
import { single, list } from "@/fx/data-helpers/misc";
import { CTX } from "@/be/context";

export type ConfigOverrides = {
  configuration_claimCreators?:     () => CLM.ClaimCreatorsConfig    [];
  configuration_sapOrderTiming?:    () => CLM.SapOrderTimingConfig   [];
  configuration_involvesTaxCredit?: () => CLM.InvolvesTaxCreditConfig[];
  configuration_claimContext?:      () => CLM.ClaimContextConfig     [];

  claimConfig?: () => CLM.ClaimConfig;

  supportedClaimTypes?: () => CLM.SupportedClaimType[];

  supportedClaimTypes_FOR_CurrentUser?: ({ currentUser }: { currentUser: CLM.User }) => CLM.SupportedClaimType[];
}

const EMPTY_CONTEXT: ConfigOverrides = {
  configuration_claimCreators:         undefined,
  configuration_sapOrderTiming:        undefined,
  configuration_involvesTaxCredit:     undefined,
  configuration_claimContext:          undefined,
  supportedClaimTypes:                 undefined,
  supportedClaimTypes_FOR_CurrentUser: undefined,
  claimConfig:                         undefined,
};
export const ConfigOverrides: ConfigOverrides = {
  ...EMPTY_CONTEXT
}
export function overrideContext(overrides: Partial<ConfigOverrides> | undefined) {
  if(overrides !== undefined) {
    Object.assign(ConfigOverrides, overrides);
  }
}
export function resetContextOverrides() {
  overrideContext(EMPTY_CONTEXT);
}

export namespace CFG {
  // #region Service

  export type Service = {

    claimConfig: ({ claimType, claimReason, itemized }: {
      claimType:   CLM.ClaimType;
      claimReason: CLM.ClaimReason;
      itemized:    boolean;
    }) => CLM.ClaimConfig;

    supportedClaimTypes: () => CLM.SupportedClaimType[];

    supportedClaimTypes_FOR_CurrentUser: () => Promise<CLM.SupportedClaimType[]>;
  }

  // #endregion
}

// #region Configuration Data

export const configuration_claimCreators     = (): CLM.ClaimCreatorsConfig    [] => ConfigOverrides.configuration_claimCreators    ?.() ?? CFG_DATA.configuration_claimCreators    ;
export const configuration_sapOrderTiming    = (): CLM.SapOrderTimingConfig   [] => ConfigOverrides.configuration_sapOrderTiming   ?.() ?? CFG_DATA.configuration_sapOrderTiming   ;
export const configuration_involvesTaxCredit = (): CLM.InvolvesTaxCreditConfig[] => ConfigOverrides.configuration_involvesTaxCredit?.() ?? CFG_DATA.configuration_involvesTaxCredit;
export const configuration_claimContext      = (): CLM.ClaimContextConfig     [] => ConfigOverrides.configuration_claimContext     ?.() ?? CFG_DATA.configuration_claimContext     ;

export function validateConfiguration() {
  return CLM.validateConfig({
    configuration_claimCreators:     configuration_claimCreators    (),
    configuration_sapOrderTiming:    configuration_sapOrderTiming   (),
    configuration_involvesTaxCredit: configuration_involvesTaxCredit(),
    configuration_claimContext:      configuration_claimContext     (),
  });
}

// #endregion

const claimConfig: ({ claimType, claimReason, itemized }: {
  claimType:   CLM.ClaimType;
  claimReason: CLM.ClaimReason;
  itemized:    boolean;
}) => CLM.ClaimConfig = ConfigOverrides.claimConfig ?? (
  ({ claimType, claimReason, itemized }) => {
    return {
      sapOrderTiming:    sapOrderTiming_FOR   ({ claimType, claimReason, itemized }),
      involvesTaxCredit: involvesTaxCredit_FOR({ claimType, claimReason           }),
    }
  }
);

function sapOrderTiming_FOR({claimType, claimReason, itemized}: {
  claimType:   CLM.ClaimType,
  claimReason: CLM.ClaimReason,
  itemized:    boolean
}): CLM.SapOrderTiming {
  return single(configuration_sapOrderTiming(), _ =>
    _.claimType   === claimType   &&
    _.claimReason === claimReason &&
    _.itemized    === itemized
  ).sapOrderTiming;
}

function involvesTaxCredit_FOR({ claimType, claimReason }: {
  claimType:   CLM.ClaimType;
  claimReason: CLM.ClaimReason
}): boolean {
  return single(configuration_involvesTaxCredit(), _ =>
    _.claimType   === claimType   &&
    _.claimReason === claimReason
  ).involvesTaxCredit;
}

export function claimContext_FROM_config({ claimType, claimReason }: {
  claimType:   CLM.ClaimType;
  claimReason: CLM.ClaimReason;
}): CLM.ClaimContextConfig {
  return single(configuration_claimContext(), _ =>
    _.claimType   === claimType   &&
    _.claimReason === claimReason
  );
}

const supportedClaimTypes: () => CLM.SupportedClaimType[] = ConfigOverrides.supportedClaimTypes ?? (
  () => configuration_sapOrderTiming().map(_ => {
    const claimContext = claimContext_FROM_config({
      claimType:   _.claimType,
      claimReason: _.claimReason
    });
    return {
      ..._,
      involvesTaxCredit: involvesTaxCredit_FOR({
        claimType:   _.claimType,
        claimReason: _.claimReason
      }),
      involvesQualityAlert:      claimContext.involvesQualityAlert,
      hasTimeSensitiveCredit:    claimContext.hasTimeSensitiveCredit,
      hasFlexibleProductRecords: claimContext.hasFlexibleProductRecords,
      requiresOrderInfo:         claimContext.requiresOrderInfo,
      requiresBbcInvoice:        claimContext.requiresBbcInvoice,
      requiresShippingInfo:      claimContext.requiresShippingInfo,
      requiresBol:               claimContext.requiresBol,
      requiresProductPhotos:     claimContext.requiresProductPhotos,
    };
  })
);

function claimCreators_FOR({ claimType, claimReason, itemized }: {
  claimType:   CLM.ClaimType,
  claimReason: CLM.ClaimReason,
  itemized:    boolean
}): RLT.RoleType[] {
  return single(configuration_claimCreators(), _ =>
    _.claimType   === claimType   &&
    _.claimReason === claimReason &&
    _.itemized    === itemized
  ).claimCreators;
}

const supportedClaimTypes_FOR_CurrentUser = ConfigOverrides.supportedClaimTypes_FOR_CurrentUser ?? (
  ({ currentUser }: { currentUser: CLM.User }): CLM.SupportedClaimType[] => {
    return list(supportedClaimTypes(), _ =>
      claimCreators_FOR({
        claimType:   _.claimType,
        claimReason: _.claimReason,
        itemized:    _.itemized
      }).includes(currentUser.roleType)
    );
  }
);

export async function configuration({ CTX }: { CTX: CTX.Service}): Promise<CFG.Service> {
  return {
    claimConfig:         claimConfig,
    supportedClaimTypes: supportedClaimTypes,

    supportedClaimTypes_FOR_CurrentUser: async () => supportedClaimTypes_FOR_CurrentUser({ currentUser: (await CTX.loggedInContext()).currentUser }),
  };
}
